import { useState, useEffect } from "react";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Filler, Tooltip, Legend } from "chart.js";
import { useApp } from "./context/AppContext";

// Views
import ListView from "./views/ListView";
import DashboardView from "./views/DashboardView";
import ContentView from "./views/ContentView";
import StructureView from "./views/StructureView";
import TzView from "./views/TzView";
import LinkGenView from "./views/LinkGenView";
import GenView from "./views/GenView";
import AllPagesMetaView from "./views/AllPagesMetaView";
import PagesOverviewView from "./views/PagesOverviewView";
import TrendsView from "./views/TrendsView";
import GscView from "./views/GscView";
import AnalyticsView from "./views/AnalyticsView";
import NgramView from "./views/NgramView";
import SeoEditorView from "./views/SeoEditorView";
import SiteScanView from "./views/SiteScanView";
import ReportAdminView from "./views/ReportAdminView";
import AiUsageView from "./views/AiUsageView";
import ErrorLogView from "./views/ErrorLogView";
import SeRankingMonitorView from "./views/SeRankingMonitorView";
import WikiLinkView from "./views/WikiLinkView";
import PageSpeedView from "./views/PageSpeedView";
import TrafficForecastView from "./views/TrafficForecastView";
import AuditView from "./views/AuditView";
import OnboardingView from "./views/OnboardingView";
import AIVisibilityView from "./views/AIVisibilityView";
import HelpView from "./views/HelpView";
import SchemaView from "./views/SchemaView";
import MetaFormulaView from "./views/MetaFormulaView";
import SdkView from "./views/SdkView";
import DirectConnectView from "./views/DirectConnectView";
import SdkFooterView from "./views/SdkFooterView";
import ArticleGenView from "./views/ArticleGenView";
import CrawlerView from "./views/CrawlerView";
import LegacyMetagoView from "./views/LegacyMetagoView";
import LostBacklinksView from "./views/LostBacklinksView";
import CollaboratorView from "./views/CollaboratorView";
import PositionsView from "./views/PositionsView";
import SemanticCleanerView from "./views/SemanticCleanerView";
import InfoBaseView from "./views/InfoBaseView";
import KanbanView from "./views/KanbanView";
import TaskTemplatesView from "./views/TaskTemplatesView";
import QABoardView from "./views/QABoardView";
import DevBoardView from "./views/DevBoardView";
import SemanticsBoardView from "./views/SemanticsBoardView";
import CopywriterView from "./views/CopywriterView";
import RedactorView from "./views/RedactorView";
import InvoiceCreatorView from "./views/InvoiceCreatorView";
import UsersView from "./views/UsersView";
import Spalah360LeadsView from "./views/Spalah360LeadsView";
import Sidebar from "./views/Sidebar";
// Combined views
import SeoCocoonCombinedView from "./views/SeoCocoonCombinedView";
import TicketsCombinedView from "./views/TicketsCombinedView";
import SemanticsCombinedView from "./views/SemanticsCombinedView";
import GscAnalysisView from "./views/GscAnalysisView";
import AeoView from "./views/AeoView";
import WorkplanView from "./views/WorkplanView";
import FinanceView from "./views/FinanceView";
import PpcCabinet from "./views/PpcCabinet";

// Components
import ToggleSwitch from "./components/ToggleSwitch";
import AiAssistant from "./components/AiAssistant";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Filler, Tooltip, Legend);

// 🥚 Пасхалка: наведення на «SPALAH» у лого → анімоване перетікання у «splaaaaash» (хвиля по буквах).
function SplashLogo() {
  return (
    <span className="sp-egg" style={{ position: "relative", display: "inline-block", whiteSpace: "nowrap" }}>
      <style>{`
.sp-egg .d,.sp-egg .s{display:inline-block;transition:opacity .22s ease, transform .3s cubic-bezier(.2,1.4,.4,1)}
.sp-egg .s{position:absolute;left:0;top:0;opacity:0;transform:translateY(2px) scale(.9);text-transform:none;letter-spacing:.5px;pointer-events:none}
.sp-egg:hover .d{opacity:0;transform:scale(1.08)}
.sp-egg:hover .s{opacity:1;transform:translateY(0) scale(1)}
.sp-egg .s b{display:inline-block;font-weight:800;color:#22d3ee;background:linear-gradient(180deg,#6366f1,#22d3ee);-webkit-background-clip:text;background-clip:text;-webkit-text-fill-color:transparent}
.sp-egg:hover .s b{animation:spWave .9s ease-in-out infinite}
@keyframes spWave{0%,100%{transform:translateY(0)}50%{transform:translateY(-4px)}}
      `}</style>
      <span className="d">SPALAH</span>
      <span className="s" aria-hidden="true">{"splaaaaash".split("").map((ch, i) => (<b key={i} style={{ animationDelay: (i * 0.055) + "s" }}>{ch}</b>))}</span>
    </span>
  );
}

export default function App() {
  const {
    view, setView, goBack, canGoBack, authed, authLoading, dark, t, toast,
    currentUser, userRole, setActiveId, activeId, proj, projects,
    loginUser, setLoginUser, loginPass, setLoginPass, loginError, setLoginError,
    loginPending, setLoginPending, loginCode, setLoginCode,
    loginNeedCode, setLoginNeedCode, loginCodeInput, setLoginCodeInput,
    handleLogin, handleVerifyCode, handleLogout,
    notifications, dismissNotification, runningTasksCount, bgTasks, projectsLoading,
  } = useApp();

  const isDemo = userRole === "demo";
  const [extInstalled, setExtInstalled] = useState(false);
  const [extBannerDismissed, setExtBannerDismissed] = useState(() => localStorage.getItem("spalah_ext_banner_dismissed") === "true");
  const [extModalOpen, setExtModalOpen] = useState(false);

  // Check if extension is installed
  useEffect(() => {
    if (!authed) return;
    const check = () => setExtInstalled(!!window.__spalah_extension || document.documentElement.hasAttribute("data-spalah-extension"));
    check();
    const timer = setTimeout(check, 1500);
    return () => clearTimeout(timer);
  }, [authed]);
  const demoAllowed = new Set([
    "list", "dashboard", "positions", "content", "structure", "seo_cocoon", "wikilink",
    "pagespeed", "ai_visibility", "site_scan", "audit",
    "schema", "aeo", "workplan", "tickets", "report_admin", "help"
  ]);

  // Invalid/paginated-out active project is resolved by the AppContext effect (fetch-by-id or reset).

  // Demo role: redirect to dashboard if accessing restricted view
  if (isDemo && !demoAllowed.has(view)) {
    setView("dashboard");
  }

  // PPC role: повністю окремий рекламний кабінет (жодного SEO-інтерфейсу).
  if (authed && userRole === "ppc") {
    return <PpcCabinet />;
  }

  const isFinance = userRole === "finance";

  // Finance role: only finance view
  if (authed && isFinance) {
    return (
      <div style={{ fontFamily: "'DM Sans', -apple-system, sans-serif", background: dark ? "#0f0f14" : "#f8f9fb", color: dark ? "#e4e4ec" : "#1a1a2e", minHeight: "100vh" }}>
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 24px", borderBottom: `1px solid ${t.border}` }}>
          <span style={{ fontSize: 18, fontWeight: 700, color: "#6366f1" }}>Spalah<span style={{ color: "#a855f7" }}>.</span> <span style={{ fontSize: 13, fontWeight: 400, color: t.textMuted }}>Фінанси</span></span>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 12, color: t.textMuted }}>{currentUser}</span>
            <button onClick={handleLogout} style={{ background: "none", border: `1px solid ${t.border}`, borderRadius: 5, padding: "3px 10px", fontSize: 11, cursor: "pointer", color: t.textMuted, fontFamily: "inherit" }}>Вихід</button>
            <ToggleSwitch />
          </div>
        </div>
        <div style={{ padding: "16px 24px" }}>
          <FinanceView />
        </div>
        {toast && <div style={{ position: "fixed", bottom: 30, left: "50%", transform: "translateX(-50%)", background: "#6366f1", color: "#fff", padding: "10px 24px", borderRadius: 10, fontSize: 13, fontWeight: 600, boxShadow: "0 4px 20px rgba(0,0,0,0.15)", zIndex: 9999 }}>{toast}</div>}
      </div>
    );
  }

  // Dev role: тільки дев-дошка (жодного SEO-інтерфейсу).
  if (authed && userRole === "dev") {
    return (
      <div style={{ fontFamily: "'DM Sans', -apple-system, sans-serif", background: dark ? "#0f0f14" : "#f8f9fb", color: dark ? "#e4e4ec" : "#1a1a2e", minHeight: "100vh" }}>
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 24px", borderBottom: `1px solid ${t.border}` }}>
          <span style={{ fontSize: 18, fontWeight: 700, color: "#6366f1" }}>Spalah<span style={{ color: "#a855f7" }}>.</span> <span style={{ fontSize: 13, fontWeight: 400, color: t.textMuted }}>Dev-дошка</span></span>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 12, color: t.textMuted }}>{currentUser}</span>
            <button onClick={handleLogout} style={{ background: "none", border: `1px solid ${t.border}`, borderRadius: 5, padding: "3px 10px", fontSize: 11, cursor: "pointer", color: t.textMuted, fontFamily: "inherit" }}>Вихід</button>
            <ToggleSwitch />
          </div>
        </div>
        <div style={{ padding: "16px 24px" }}>
          <DevBoardView />
        </div>
        {toast && <div style={{ position: "fixed", bottom: 30, left: "50%", transform: "translateX(-50%)", background: "#6366f1", color: "#fff", padding: "10px 24px", borderRadius: 10, fontSize: 13, fontWeight: 600, boxShadow: "0 4px 20px rgba(0,0,0,0.15)", zIndex: 9999 }}>{toast}</div>}
      </div>
    );
  }

  // Sem role: тільки дошка семантики (жодного SEO-інтерфейсу).
  if (authed && userRole === "sem") {
    return (
      <div style={{ fontFamily: "'DM Sans', -apple-system, sans-serif", background: dark ? "#0f0f14" : "#f8f9fb", color: dark ? "#e4e4ec" : "#1a1a2e", minHeight: "100vh" }}>
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 24px", borderBottom: `1px solid ${t.border}` }}>
          <span style={{ fontSize: 18, fontWeight: 700, color: "#0ea5e9" }}>Spalah<span style={{ color: "#06b6d4" }}>.</span> <span style={{ fontSize: 13, fontWeight: 400, color: t.textMuted }}>Дошка семантики</span></span>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 12, color: t.textMuted }}>{currentUser}</span>
            <button onClick={handleLogout} style={{ background: "none", border: `1px solid ${t.border}`, borderRadius: 5, padding: "3px 10px", fontSize: 11, cursor: "pointer", color: t.textMuted, fontFamily: "inherit" }}>Вихід</button>
            <ToggleSwitch />
          </div>
        </div>
        <div style={{ padding: "16px 24px" }}>
          <SemanticsBoardView />
        </div>
        {toast && <div style={{ position: "fixed", bottom: 30, left: "50%", transform: "translateX(-50%)", background: "#0ea5e9", color: "#fff", padding: "10px 24px", borderRadius: 10, fontSize: 13, fontWeight: 600, boxShadow: "0 4px 20px rgba(0,0,0,0.15)", zIndex: 9999 }}>{toast}</div>}
      </div>
    );
  }

  // Copywriter role: ізольований кабінет текстів (жодного SEO-інтерфейсу).
  if (authed && userRole === "copywriter") {
    return (
      <div style={{ fontFamily: "'DM Sans', -apple-system, sans-serif", background: dark ? "#0f0f14" : "#f8f9fb", color: dark ? "#e4e4ec" : "#1a1a2e", minHeight: "100vh" }}>
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 24px", borderBottom: `1px solid ${t.border}` }}>
          <span style={{ fontSize: 18, fontWeight: 700, color: "#16a34a" }}>Spalah<span style={{ color: "#22c55e" }}>.</span> <span style={{ fontSize: 13, fontWeight: 400, color: t.textMuted }}>Кабінет копірайтера</span></span>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 12, color: t.textMuted }}>{currentUser}</span>
            <button onClick={handleLogout} style={{ background: "none", border: `1px solid ${t.border}`, borderRadius: 5, padding: "3px 10px", fontSize: 11, cursor: "pointer", color: t.textMuted, fontFamily: "inherit" }}>Вихід</button>
            <ToggleSwitch />
          </div>
        </div>
        <div style={{ padding: "16px 24px" }}>
          <CopywriterView />
        </div>
        {toast && <div style={{ position: "fixed", bottom: 30, left: "50%", transform: "translateX(-50%)", background: "#16a34a", color: "#fff", padding: "10px 24px", borderRadius: 10, fontSize: 13, fontWeight: 600, boxShadow: "0 4px 20px rgba(0,0,0,0.15)", zIndex: 9999 }}>{toast}</div>}
      </div>
    );
  }

  // Redactor role: ізольований кабінет перевірки текстів.
  if (authed && userRole === "redactor") {
    return (
      <div style={{ fontFamily: "'DM Sans', -apple-system, sans-serif", background: dark ? "#0f0f14" : "#f8f9fb", color: dark ? "#e4e4ec" : "#1a1a2e", minHeight: "100vh" }}>
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 24px", borderBottom: `1px solid ${t.border}` }}>
          <span style={{ fontSize: 18, fontWeight: 700, color: "#a855f7" }}>Spalah<span style={{ color: "#c084fc" }}>.</span> <span style={{ fontSize: 13, fontWeight: 400, color: t.textMuted }}>Кабінет редактора</span></span>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 12, color: t.textMuted }}>{currentUser}</span>
            <button onClick={handleLogout} style={{ background: "none", border: `1px solid ${t.border}`, borderRadius: 5, padding: "3px 10px", fontSize: 11, cursor: "pointer", color: t.textMuted, fontFamily: "inherit" }}>Вихід</button>
            <ToggleSwitch />
          </div>
        </div>
        <div style={{ padding: "16px 24px" }}>
          <RedactorView />
        </div>
        {toast && <div style={{ position: "fixed", bottom: 30, left: "50%", transform: "translateX(-50%)", background: "#a855f7", color: "#fff", padding: "10px 24px", borderRadius: 10, fontSize: 13, fontWeight: 600, boxShadow: "0 4px 20px rgba(0,0,0,0.15)", zIndex: 9999 }}>{toast}</div>}
      </div>
    );
  }

  // ── Loading ──
  if (authLoading || (authed && activeId && view !== "list" && !proj)) {
    return (
      <div style={{ fontFamily: "'DM Sans', -apple-system, sans-serif", display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", background: dark ? "#0f0f14" : "#f8f9fb", color: dark ? "#e4e4ec" : "#1a1a2e" }}>
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
        <div style={{ fontSize: 14 }}>Loading...</div>
      </div>
    );
  }

  // ── Login ──
  if (!authed) {
    return (
      <div style={{ fontFamily: "'Inter', -apple-system, sans-serif", display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", background: dark ? "#0a0a0c" : "#f5f5f7", color: dark ? "#ededf0" : "#0a0a0f", transition: "background .2s" }}>
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
        <div style={{ width: 380, padding: 36, background: dark ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.7)", borderRadius: 12, border: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`, backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, marginBottom: 28 }}>
            <img src="/logo.png" alt="SEO SPALAH" style={{ width: 36, height: 36, objectFit: "contain", filter: dark ? "invert(1)" : "none" }} />
            <span style={{ fontWeight: 700, fontSize: 13, letterSpacing: "3px", textTransform: "uppercase", opacity: 0.7 }}>SEO SPALAH</span>
          </div>
          {loginPending && loginNeedCode ? (
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Enter verification code</div>
              <div style={{ fontSize: 13, color: dark ? "#888" : "#999", marginBottom: 16 }}>Code sent to Telegram. Enter it below.</div>
              <input
                type="text" inputMode="numeric" maxLength={6} placeholder="000000" value={loginCodeInput}
                onChange={e => setLoginCodeInput(e.target.value.replace(/\D/g, "").slice(0, 6))}
                style={{ border: `1px solid ${dark ? "#333346" : "#e0e0e0"}`, borderRadius: 8, padding: "12px 14px", fontSize: 22, fontWeight: 700, letterSpacing: 8, outline: "none", width: "100%", fontFamily: "inherit", background: dark ? "#1e1e2c" : "#fff", color: dark ? "#e4e4ec" : "#1a1a2e", marginBottom: 12, boxSizing: "border-box", textAlign: "center" }}
                autoFocus
                onKeyDown={e => { if (e.key === "Enter" && loginCodeInput.length === 6) handleVerifyCode(); }}
              />
              {loginError && <div style={{ color: "#ef4444", fontSize: 13, marginBottom: 12 }}>{loginError}</div>}
              <button onClick={handleVerifyCode} disabled={loginCodeInput.length !== 6} style={{ background: loginCodeInput.length === 6 ? "linear-gradient(135deg, #6366f1, #8b5cf6)" : (dark ? "#333" : "#ccc"), color: "#fff", border: "none", borderRadius: 8, padding: "10px 0", fontSize: 14, fontWeight: 600, cursor: loginCodeInput.length === 6 ? "pointer" : "default", width: "100%", marginBottom: 8 }}>
                Confirm
              </button>
              <button onClick={() => { setLoginPending(false); setLoginCode(""); setLoginNeedCode(false); setLoginCodeInput(""); setLoginError(""); }} style={{ background: "none", border: `1px solid ${dark ? "#333346" : "#e0e0e0"}`, borderRadius: 8, padding: "8px 20px", fontSize: 13, cursor: "pointer", color: dark ? "#e4e4ec" : "#1a1a2e", fontFamily: "inherit" }}>
                Cancel
              </button>
            </div>
          ) : loginPending ? (
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <div style={{ width: 40, height: 40, border: "3px solid transparent", borderTop: "3px solid #6366f1", borderRadius: "50%", animation: "spin 1s linear infinite", margin: "0 auto 16px" }} />
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Confirm login in Telegram</div>
              <div style={{ fontSize: 13, color: dark ? "#888" : "#999" }}>Waiting for admin approval...</div>
              <button onClick={() => { setLoginPending(false); setLoginCode(""); }} style={{ marginTop: 16, background: "none", border: `1px solid ${dark ? "#333346" : "#e0e0e0"}`, borderRadius: 8, padding: "8px 20px", fontSize: 13, cursor: "pointer", color: dark ? "#e4e4ec" : "#1a1a2e", fontFamily: "inherit" }}>
                Cancel
              </button>
            </div>
          ) : (
            <form onSubmit={handleLogin}>
              <input
                type="text" placeholder="Login" value={loginUser}
                onChange={e => setLoginUser(e.target.value)}
                style={{ border: `1px solid ${dark ? "#333346" : "#e0e0e0"}`, borderRadius: 8, padding: "10px 14px", fontSize: 14, outline: "none", width: "100%", fontFamily: "inherit", background: dark ? "#1e1e2c" : "#fff", color: dark ? "#e4e4ec" : "#1a1a2e", marginBottom: 12, boxSizing: "border-box" }}
              />
              <input
                type="password" placeholder="Password" value={loginPass}
                onChange={e => setLoginPass(e.target.value)}
                style={{ border: `1px solid ${dark ? "#333346" : "#e0e0e0"}`, borderRadius: 8, padding: "10px 14px", fontSize: 14, outline: "none", width: "100%", fontFamily: "inherit", background: dark ? "#1e1e2c" : "#fff", color: dark ? "#e4e4ec" : "#1a1a2e", marginBottom: 16, boxSizing: "border-box" }}
              />
              {loginError && <div style={{ color: "#ef4444", fontSize: 13, marginBottom: 12, textAlign: "center" }}>{loginError}</div>}
              <button type="submit" style={{ background: "linear-gradient(135deg, #6366f1, #8b5cf6)", color: "#fff", border: "none", borderRadius: 8, padding: "10px 0", fontSize: 14, fontWeight: 600, cursor: "pointer", width: "100%" }}>
                Log in
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  // ═══ MAIN RENDER ═══
  return (
    <div style={{ fontFamily: "'Inter', -apple-system, sans-serif", width: "100%", margin: "0 auto", padding: "0", background: t.bg, color: t.text, minHeight: "100vh", transition: "background .2s, color .2s" }}>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 20px", borderBottom: `1px solid ${t.border}`, background: dark ? "rgba(10,10,12,0.8)" : "rgba(245,245,247,0.8)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)", position: "sticky", top: 0, zIndex: 100 }}>
        <div onClick={() => { setActiveId(null); setView("list"); }} style={{ cursor: "pointer", display: "flex", alignItems: "center" }}>
          <img src="/logo.png" alt="SEO SPALAH" style={{ width: 22, height: 22, objectFit: "contain", filter: dark ? "invert(1)" : "none" }} />
        </div>
        <span onClick={() => { setActiveId(null); setView("list"); }} style={{ fontWeight: 700, fontSize: 12, cursor: "pointer", letterSpacing: "2px", textTransform: "uppercase", opacity: 0.8 }}>SEO <SplashLogo /></span>
        {canGoBack && (
          <button onClick={goBack} style={{ background: "none", border: `1px solid ${t.border}`, borderRadius: 5, padding: "3px 8px", cursor: "pointer", color: t.textMuted, fontFamily: "inherit", fontSize: 11, display: "inline-flex", alignItems: "center", gap: 4, marginLeft: 8, transition: "border-color .15s" }}
            title="Назад">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
        )}
        <div style={{ flex: 1 }} />
        {runningTasksCount > 0 && (() => {
          const running = bgTasks.filter(bt => bt.status === "running");
          return (
            <div style={{ position: "relative" }} className="task-badge-wrap">
              <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 5, background: dark ? "rgba(59,130,246,0.08)" : "rgba(37,99,235,0.05)", border: `1px solid ${dark ? "rgba(59,130,246,0.15)" : "rgba(37,99,235,0.1)"}`, fontSize: 11, fontWeight: 600, color: t.accent, cursor: "default" }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#6366f1", animation: "pulse 1.5s infinite" }} />
                {runningTasksCount} {runningTasksCount === 1 ? "задача" : "задач"}
              </div>
              <div className="task-badge-popup" style={{
                position: "absolute", top: "100%", right: 0, marginTop: 6, minWidth: 320, maxWidth: 400,
                background: dark ? "#1a1a2e" : "#fff", border: `1px solid ${t.border}`, borderRadius: 12,
                boxShadow: "0 8px 30px rgba(0,0,0,0.15)", zIndex: 9999, padding: 8,
                display: "none",
              }}>
                {running.map(bt => {
                  const p = bt.progress || {};
                  const pct = p.total ? Math.round((p.done || p.current || 0) / p.total * 100) : 0;
                  const TASK_LABELS = {
                    meta_generation: "Мета-теги",
                    site_scan: "Сканер сайту",
                    ngram_analysis: "N-грам аналіз",
                    ngram_compare: "Порівняння N-грам",
                    pagespeed_check: "PageSpeed",
                    audit_run: "SEO Audit 360",
                    gsc_coverage_check: "GSC Coverage",
                    semantic_collector: "Збір семантики",
                    seranking_sync: "SE Ranking",
                    schema_generate: "Schema.org",
                    llms_generate: "llms.txt",
                    llms_full_generate: "llms-full.txt",
                    faq_generate: "FAQ генерація",
                    positions_check: "Перевірка позицій",
                    robots_ai: "robots.txt AI",
                  };
                  return (
                    <div key={bt.id} style={{ padding: "8px 10px", borderRadius: 8, marginBottom: 4, background: dark ? "rgba(99,102,241,0.06)" : "rgba(99,102,241,0.03)" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                        <span style={{ fontSize: 12, fontWeight: 700 }}>{TASK_LABELS[bt.type] || bt.title || bt.type}</span>
                        <span style={{ fontSize: 11, fontWeight: 700, color: "#6366f1" }}>{pct}%</span>
                      </div>
                      <div style={{ height: 4, borderRadius: 2, background: dark ? "#252535" : "#e5e7eb", overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${pct}%`, background: "linear-gradient(90deg, #6366f1, #8b5cf6)", borderRadius: 2, transition: "width .3s" }} />
                      </div>
                      {(p.url || p.message) && (
                        <div style={{ fontSize: 10, color: p.queued ? "#f59e0b" : t.textMuted, marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: p.queued ? 600 : 400 }}>
                          {p.queued ? "⏳ " : ""}{p.message || p.url}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}
        {currentUser && <span style={{ fontSize: 12, color: t.textMuted }}>{currentUser}</span>}
        {!isDemo && currentUser === "iz@svitlo.agency" && <button onClick={() => { setActiveId(null); setView("invoice_creator"); }} style={{ background: view === "invoice_creator" ? "linear-gradient(135deg,#6366f1,#a855f7)" : "none", border: `1px solid ${view === "invoice_creator" ? "#6366f1" : t.border}`, borderRadius: 8, padding: "4px 12px", fontSize: 12, cursor: "pointer", color: view === "invoice_creator" ? "#fff" : t.textMuted, fontFamily: "inherit", fontWeight: 600 }}>Рахунки</button>}
        {!isDemo && userRole === "admin" && <button onClick={() => { setActiveId(null); setView("finance"); }} style={{ background: view === "finance" ? "linear-gradient(135deg,#6366f1,#a855f7)" : "none", border: `1px solid ${view === "finance" ? "#6366f1" : t.border}`, borderRadius: 8, padding: "4px 12px", fontSize: 12, cursor: "pointer", color: view === "finance" ? "#fff" : t.textMuted, fontFamily: "inherit", fontWeight: 600 }}>Фінанси</button>}
        {!isDemo && <button onClick={() => { setActiveId(null); setView("qa_board"); }} style={{ background: view === "qa_board" ? "linear-gradient(135deg,#6366f1,#a855f7)" : "none", border: `1px solid ${view === "qa_board" ? "#6366f1" : t.border}`, borderRadius: 8, padding: "4px 12px", fontSize: 12, cursor: "pointer", color: view === "qa_board" ? "#fff" : t.textMuted, fontFamily: "inherit", fontWeight: 600 }}>QA-доска</button>}
        {!isDemo && <button onClick={() => { setActiveId(null); setView("dev_board"); }} style={{ background: view === "dev_board" ? "linear-gradient(135deg,#6366f1,#a855f7)" : "none", border: `1px solid ${view === "dev_board" ? "#6366f1" : t.border}`, borderRadius: 8, padding: "4px 12px", fontSize: 12, cursor: "pointer", color: view === "dev_board" ? "#fff" : t.textMuted, fontFamily: "inherit", fontWeight: 600 }}>Dev-доска</button>}
        {!isDemo && <button onClick={() => { setActiveId(null); setView("semantics_board"); }} style={{ background: view === "semantics_board" ? "linear-gradient(135deg,#0ea5e9,#06b6d4)" : "none", border: `1px solid ${view === "semantics_board" ? "#0ea5e9" : t.border}`, borderRadius: 8, padding: "4px 12px", fontSize: 12, cursor: "pointer", color: view === "semantics_board" ? "#fff" : t.textMuted, fontFamily: "inherit", fontWeight: 600 }}>📚 Збір семантики</button>}
        {!isDemo && currentUser === "iz@svitlo.agency" && (
          <button onClick={() => { setActiveId(null); setView("spalah360_leads"); }} style={{ background: view === "spalah360_leads" ? "linear-gradient(135deg,#a855f7,#ec4899)" : "none", border: `1px solid ${view === "spalah360_leads" ? "#a855f7" : t.border}`, borderRadius: 8, padding: "4px 12px", fontSize: 12, cursor: "pointer", color: view === "spalah360_leads" ? "#fff" : t.textMuted, fontFamily: "inherit", fontWeight: 600 }}>🎯 360 Ліди</button>
        )}
        {!isDemo && currentUser === "iz@svitlo.agency" && (
          <button onClick={() => { setActiveId(null); setView("users_admin"); }} style={{ background: view === "users_admin" ? "linear-gradient(135deg,#6366f1,#a855f7)" : "none", border: `1px solid ${view === "users_admin" ? "#6366f1" : t.border}`, borderRadius: 8, padding: "4px 12px", fontSize: 12, cursor: "pointer", color: view === "users_admin" ? "#fff" : t.textMuted, fontFamily: "inherit", fontWeight: 600 }}>Команда</button>
        )}
        {!isDemo && userRole === "admin" && (
          <button onClick={() => { setActiveId(null); setView("seranking_monitor"); }} style={{ background: view === "seranking_monitor" ? "linear-gradient(135deg,#6366f1,#a855f7)" : "none", border: `1px solid ${view === "seranking_monitor" ? "#6366f1" : t.border}`, borderRadius: 8, padding: "4px 12px", fontSize: 12, cursor: "pointer", color: view === "seranking_monitor" ? "#fff" : t.textMuted, fontFamily: "inherit", fontWeight: 600 }}>Monitor</button>
        )}
        {!isDemo && userRole === "admin" && (
          <button onClick={() => { setActiveId(null); setView("ai_usage"); }} style={{ background: view === "ai_usage" ? "linear-gradient(135deg,#6366f1,#a855f7)" : "none", border: `1px solid ${view === "ai_usage" ? "#6366f1" : t.border}`, borderRadius: 8, padding: "4px 12px", fontSize: 12, cursor: "pointer", color: view === "ai_usage" ? "#fff" : t.textMuted, fontFamily: "inherit", fontWeight: 600 }}>💰 AI Витрати</button>
        )}
        {!isDemo && userRole === "admin" && (
          <button onClick={() => { setActiveId(null); setView("error_log"); }} style={{ background: view === "error_log" ? "linear-gradient(135deg,#6366f1,#a855f7)" : "none", border: `1px solid ${view === "error_log" ? "#6366f1" : t.border}`, borderRadius: 8, padding: "4px 12px", fontSize: 12, cursor: "pointer", color: view === "error_log" ? "#fff" : t.textMuted, fontFamily: "inherit", fontWeight: 600 }}>🐞 Errors</button>
        )}
        {!isDemo && userRole === "admin" && (
          <button onClick={() => setExtModalOpen(true)} title="Spalah Chrome Extension" style={{ background: "none", border: `1px solid ${t.border}`, borderRadius: 8, padding: "4px 12px", fontSize: 12, cursor: "pointer", color: t.textMuted, fontFamily: "inherit", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="4"/><line x1="21.17" y1="8" x2="12" y2="8"/><line x1="3.95" y1="6.06" x2="8.54" y2="14"/><line x1="10.88" y1="21.94" x2="15.46" y2="14"/></svg>
            Розширення
          </button>
        )}
        <button onClick={handleLogout} style={{ background: "none", border: `1px solid ${t.border}`, borderRadius: 5, padding: "3px 10px", fontSize: 11, cursor: "pointer", color: t.textMuted, fontFamily: "inherit", transition: "border-color .15s" }}>Exit</button>
        <ToggleSwitch />
      </div>

      <div style={{ padding: "16px 24px" }}>
      {view === "list" && !extInstalled && !extBannerDismissed && !isDemo && authed && (
        <div style={{ margin: "0 0 16px", padding: "14px 20px", borderRadius: 12, background: dark ? "linear-gradient(135deg, rgba(99,102,241,0.08), rgba(139,92,246,0.06))" : "linear-gradient(135deg, rgba(99,102,241,0.06), rgba(139,92,246,0.04))", border: `1px solid ${dark ? "rgba(99,102,241,0.15)" : "rgba(99,102,241,0.12)"}`, display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: "linear-gradient(135deg, #6366f1, #8b5cf6)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>Spalah SEO Extension</div>
            <div style={{ fontSize: 11, color: t.textMuted, lineHeight: 1.4 }}>
              Встановіть розширення для Chrome — автоматичний SEO-аналіз сторінок, перевірка canonical, визначення CMS, Core Web Vitals та Schema.org
            </div>
          </div>
          <a href="/extension.zip" download style={{ background: "linear-gradient(135deg, #6366f1, #8b5cf6)", color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 12, fontWeight: 600, cursor: "pointer", textDecoration: "none", whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 6 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Завантажити
          </a>
          <button onClick={() => { setExtBannerDismissed(true); localStorage.setItem("spalah_ext_banner_dismissed", "true"); }} style={{ background: "none", border: "none", cursor: "pointer", color: t.textMuted, padding: 4, fontSize: 16, lineHeight: 1, opacity: 0.5 }} title="Закрити">&times;</button>
        </div>
      )}
      {view === "list" && <ListView />}
      {view === "invoice_creator" && <InvoiceCreatorView />}
      {view === "article_gen" && <ArticleGenView />}
      {view === "finance" && <FinanceView />}
      {view === "qa_board" && <QABoardView />}
      {view === "dev_board" && <DevBoardView />}
      {view === "semantics_board" && <SemanticsBoardView />}
      {view === "users_admin" && <UsersView />}
      {view === "spalah360_leads" && <Spalah360LeadsView />}
      {view === "seranking_monitor" && <SeRankingMonitorView />}
      {view === "ai_usage" && <AiUsageView />}
      {view === "error_log" && <ErrorLogView />}
      {!["list", "seranking_monitor", "qa_board", "dev_board", "semantics_board", "users_admin", "invoice_creator", "finance", "ai_usage", "error_log", "spalah360_leads"].includes(view) && (
        <div style={{ display: "flex", gap: 0 }}>
          <Sidebar />
          <div style={{ flex: 1, minWidth: 0, paddingLeft: 20 }}>
            {view === "dashboard" && <DashboardView />}
            {view === "gen" && <GenView />}
            {view === "all_pages_meta" && <AllPagesMetaView />}
            {view === "pages_overview" && <PagesOverviewView />}
            {view === "trends" && <TrendsView />}
            {view === "content" && <ContentView />}
            {view === "structure" && <StructureView />}
            {view === "tz" && <TzView />}
            {view === "linkgen" && <LinkGenView />}
            {view === "gsc" && <GscView />}
            {view === "analytics" && <AnalyticsView />}
            {view === "ngrams" && <NgramView />}
            {view === "seo_editor" && <SeoEditorView />}
            {view === "site_scan" && <SiteScanView />}
            {view === "report_admin" && <ReportAdminView />}
            {view === "wikilink" && <WikiLinkView />}
            {view === "pagespeed" && <PageSpeedView />}
            {view === "audit" && (activeId === "fp5nh7qw" || currentUser === "iz@svitlo.agency" || currentUser === "ib@svitlo.agency") && <AuditView />}
            {view === "onboarding" && <OnboardingView />}
            {view === "ai_visibility" && <AIVisibilityView />}
            {view === "help" && <HelpView />}
            {view === "schema" && <SchemaView />}
            {view === "meta_formula" && <MetaFormulaView />}
            {view === "sdk" && <SdkView />}
            {view === "direct_connect" && <DirectConnectView />}
            {view === "sdk_footer" && <SdkFooterView />}
            {view === "crawler" && <CrawlerView />}
            {view === "legacy_metago" && <LegacyMetagoView />}
            {view === "lost_backlinks" && <LostBacklinksView />}
            {view === "collaborator" && <CollaboratorView />}
            {view === "positions" && <PositionsView />}
            {view === "traffic_forecast" && <TrafficForecastView />}
            {view === "semantic_cleaner" && <SemanticCleanerView />}
            {view === "info_base" && <InfoBaseView />}
            {view === "kanban" && <KanbanView />}
            {view === "task_templates" && <TaskTemplatesView />}
            {/* Combined views */}
            {view === "semantics" && <SemanticsCombinedView />}
            {view === "seo_cocoon" && <SeoCocoonCombinedView />}
            {view === "tickets" && <TicketsCombinedView />}
            {view === "gsc_analysis" && <GscAnalysisView />}
            {view === "aeo" && <AeoView />}
            {view === "workplan" && <WorkplanView />}
          </div>
          <AiAssistant />
        </div>
      )}

      {toast && <div style={{ position: "fixed", bottom: 20, left: "50%", transform: "translateX(-50%)", background: "#1f2937", color: "#fff", padding: "8px 20px", borderRadius: 10, fontSize: 13, zIndex: 1000, animation: "fadeIn .2s" }}>{toast}</div>}

      {notifications.length > 0 && (
        <div style={{ position: "fixed", top: 16, right: 16, zIndex: 9999, display: "flex", flexDirection: "column", gap: 8, maxWidth: 360 }}>
          {notifications.map(n => (
            <div
              key={n.id}
              onClick={() => dismissNotification(n.id)}
              style={{
                padding: "12px 16px", borderRadius: 10, cursor: "pointer",
                background: n.type === "success" ? (dark ? "#064e3b" : "#ecfdf5") : (dark ? "#7f1d1d" : "#fef2f2"),
                border: `1px solid ${n.type === "success" ? (dark ? "#10b981" : "#6ee7b7") : (dark ? "#ef4444" : "#fca5a5")}`,
                boxShadow: "0 4px 16px rgba(0,0,0,.15)",
                animation: "slideIn .3s ease",
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 600, color: n.type === "success" ? "#10b981" : "#ef4444", marginBottom: 2 }}>
                {n.type === "success" ? "Готово" : "Ошибка"}
              </div>
              <div style={{ fontSize: 12, color: t.text }}>{n.title}</div>
              {n.error && <div style={{ fontSize: 11, color: "#ef4444", marginTop: 2 }}>{n.error}</div>}
            </div>
          ))}
        </div>
      )}

      </div>
      {/* Footer */}
      <div style={{ borderTop: `1px solid ${t.border}`, padding: "14px 24px", display: "flex", alignItems: "center", justifyContent: "center", gap: 12 }}>
        <img src="/logo.png" alt="" style={{ width: 14, height: 14, objectFit: "contain", filter: dark ? "invert(1)" : "none", opacity: 0.25 }} />
        <span style={{ fontSize: 9, color: t.textMuted, letterSpacing: "2.5px", textTransform: "uppercase", opacity: 0.35, fontWeight: 500 }}>SEO SPALAH</span>
        <span style={{ width: 1, height: 8, background: t.textMuted, opacity: 0.15 }} />
        <a href="https://svitlo.agency" target="_blank" rel="noopener noreferrer" style={{ fontSize: 9, color: t.textMuted, textDecoration: "none", opacity: 0.3, letterSpacing: "0.5px" }}>svitlo.agency</a>
      </div>

      {extModalOpen && (
        <div onClick={() => setExtModalOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: dark ? "#0c0c10" : "#fff", border: `1px solid ${t.border}`, borderRadius: 16, padding: 28, maxWidth: 460, width: "92%", boxShadow: "0 20px 60px rgba(0,0,0,0.4)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18 }}>
              <div style={{ width: 48, height: 48, borderRadius: 12, background: "linear-gradient(135deg, #6366f1, #8b5cf6)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="4"/><line x1="21.17" y1="8" x2="12" y2="8"/><line x1="3.95" y1="6.06" x2="8.54" y2="14"/><line x1="10.88" y1="21.94" x2="15.46" y2="14"/></svg>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 17, fontWeight: 700, color: t.text, marginBottom: 2 }}>Spalah SEO Extension</div>
                <div style={{ fontSize: 11, color: t.textMuted }}>Chrome / Edge / Brave · v1.1.0</div>
              </div>
              {extInstalled && (
                <span style={{ background: "rgba(16,185,129,0.15)", color: "#10b981", padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600 }}>Встановлено</span>
              )}
            </div>

            <div style={{ fontSize: 12, color: t.textMuted, lineHeight: 1.6, marginBottom: 18 }}>
              Аналіз сторінки на льоту: meta, canonical, CWV, Schema.org, CMS, заголовки. <br/>
              <strong style={{ color: t.text }}>Нове в 1.1.0:</strong> вкладка <strong style={{ color: t.text }}>XPath</strong> — клацни на блок на сторінці і отримай готовий селектор для парсингу (один або декілька).
            </div>

            <a href="/extension.zip" download style={{ display: "block", textAlign: "center", background: "linear-gradient(135deg, #6366f1, #8b5cf6)", color: "#fff", border: "none", borderRadius: 10, padding: "12px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer", textDecoration: "none", marginBottom: 12 }}>
              Завантажити .zip
            </a>

            <div style={{ background: dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.03)", border: `1px solid ${t.border}`, borderRadius: 8, padding: 12, fontSize: 11, color: t.textMuted, lineHeight: 1.7, marginBottom: 14 }}>
              <strong style={{ color: t.text }}>Як встановити:</strong><br/>
              1. Розпакуй архів у будь-яку папку<br/>
              2. Відкрий <code style={{ background: dark ? "#1a1a22" : "#f0f0f4", padding: "1px 5px", borderRadius: 3 }}>chrome://extensions</code><br/>
              3. Увімкни <em>Режим розробника</em> (правий верхній кут)<br/>
              4. Натисни <em>Завантажити розпаковане</em> → обери папку
            </div>

            <button onClick={() => setExtModalOpen(false)} style={{ width: "100%", background: "none", border: `1px solid ${t.border}`, borderRadius: 8, padding: "8px 14px", fontSize: 12, cursor: "pointer", color: t.textMuted, fontFamily: "inherit", fontWeight: 600 }}>Закрити</button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
        @keyframes slideIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        .task-badge-wrap:hover .task-badge-popup { display: block !important; }
      `}</style>
    </div>
  );
}
